import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * SkillAnalyzerHeader — hero banner following the approved Tools header pattern:
 * 1. Glows blur-3xl (violet/indigo)
 * 2. Grid texture overlay
 * 3. Status tag with LED blink
 * 4. Title gradient (violet→indigo, font-black italic)
 * 5. Subtitle monospace
 * 6. Decorative element (Venn-style overlapping circles)
 */
const SkillAnalyzerHeader = () => {
    const { t } = useTranslation();

    return (
        <div className="mb-10">
            {/* HERO BANNER */}
            <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-slate-900
                shadow-xl dark:shadow-2xl border border-slate-200 dark:border-slate-800
                group transition-colors duration-300">

                {/* 1. Glow backgrounds */}
                <div className="absolute -top-24 -right-24 w-96 h-96
                    bg-violet-500/10 dark:bg-violet-600/20 rounded-full blur-3xl
                    mix-blend-multiply dark:mix-blend-screen
                    group-hover:bg-violet-400/20 dark:group-hover:bg-violet-500/30
                    transition-all duration-1000" />
                <div className="absolute -bottom-24 -left-24 w-96 h-96
                    bg-indigo-500/10 dark:bg-indigo-600/20 rounded-full blur-3xl
                    mix-blend-multiply dark:mix-blend-screen
                    group-hover:bg-indigo-400/20 dark:group-hover:bg-indigo-500/30
                    transition-all duration-1000" />

                {/* 2. Grid texture overlay */}
                <div className="absolute inset-0
                    bg-[linear-gradient(rgba(139,92,246,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.04)_1px,transparent_1px)]
                    dark:bg-[linear-gradient(rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.08)_1px,transparent_1px)]
                    bg-[size:32px_32px]
                    [mask-image:radial-gradient(ellipse_at_center,black_50%,transparent_100%)]" />

                <div className="relative z-10 px-8 py-10 md:p-12 flex flex-col items-center justify-center text-center gap-6">
                    <div className="relative w-full max-w-4xl mx-auto flex flex-col items-center">

                        {/* 3. Status tag with LED blink */}
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full
                            bg-violet-500/10 border border-violet-500/20
                            text-violet-600 dark:text-violet-400
                            text-xs font-mono font-bold tracking-widest uppercase mb-4 shadow-inner">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-500" />
                            </span>
                            Analytical Tool
                        </div>

                        {/* 4. Title gradient */}
                        <h1 className="text-4xl md:text-6xl font-black italic
                            text-transparent bg-clip-text
                            bg-gradient-to-r from-violet-500 to-indigo-600
                            dark:from-violet-400 dark:to-indigo-500
                            tracking-tighter mb-4 drop-shadow-sm select-none">
                            {t('SkillAnalyzer.title', 'Skill Analyzer')}
                        </h1>

                        {/* 5. Subtitle monospace */}
                        <p className="text-base md:text-lg text-slate-600 dark:text-slate-400
                            font-mono max-w-2xl leading-relaxed
                            border-b border-violet-500/20 pb-4
                            transition-colors duration-300">
                            {t('SkillAnalyzer.subtitle', '> ANALYZE _ INTERSECT _ COMPARE')}
                        </p>

                        {/* 6. Decorative element — Venn diagram circles */}
                        {/* Left Venn */}
                        <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-4
                            hidden xl:flex items-center justify-center w-28 h-28
                            opacity-30 dark:opacity-20 pointer-events-none">
                            <div className="absolute w-16 h-16 rounded-full border border-violet-500/60
                                animate-[spin_20s_linear_infinite] translate-x-3" />
                            <div className="absolute w-16 h-16 rounded-full border border-indigo-500/60
                                animate-[spin_20s_linear_infinite_reverse] -translate-x-3" />
                            <div className="absolute w-10 h-16 bg-violet-500/10 rounded-full blur-sm" />
                        </div>
                        {/* Right Venn */}
                        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-4
                            hidden xl:flex items-center justify-center w-28 h-28
                            opacity-30 dark:opacity-20 pointer-events-none">
                            <div className="absolute w-16 h-16 rounded-full border border-indigo-500/60
                                animate-[spin_20s_linear_infinite] translate-x-3" />
                            <div className="absolute w-16 h-16 rounded-full border border-violet-500/60
                                animate-[spin_20s_linear_infinite_reverse] -translate-x-3" />
                            <div className="absolute w-10 h-16 bg-indigo-500/10 rounded-full blur-sm" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkillAnalyzerHeader;
